import React from 'react';
import {Stack, styled} from '@mui/material';
import {
  Body,
  Button,
  Headline,
  Modal,
  designSystemToken,
} from '@lightricks/react-design-system';
import formatCentsToDollars from '@/utils/formatCentsToDollars';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {ConversationMessage} from '@/types/conversationMessage';
import type {NegotiationSnapshot} from '@/types/negotiationSnapshot';
import FlashMessage from '@/components/flash-message';
import GigDeliverableChangeRow from '@/components/gig-deliverable-change-row';
import useWithdrawBrandNegotiation from '@/hooks/mutations/use-withdraw-brand-negotiation';

const SnapshotCard = styled(Stack)({
  border: '1px solid',
  borderRadius: '6px',
  padding: '15px',
  marginTop: '8px',
});

const SectionHeader = styled(Body)({
  marginTop: '10px',
  textAlign: 'left',
});

const ValuesRow = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'baseline',
  gap: '6px',
  flexWrap: 'wrap',
});

const ButtonGroup = styled(Stack)({
  gap: '8px',
});

const StruckBody = styled(Body)({
  textDecoration: 'line-through',
});

type WithdrawNegotiationOfferModalProps = {
  open: boolean;
  onClose: () => void;
  brandNegotiationId: string;
  paidGigId: string | null;
  networkPrettyName?: string | null;
  snapshot: Pick<
    NegotiationSnapshot,
    | 'costInCents'
    | 'previousCostInCents'
    | 'contentAgreementTimePeriodInMonths'
    | 'previousContentAgreementTimePeriodInMonths'
    | 'gigDeliverableChanges'
  >;
  onWithdrawSuccess?: (message: ConversationMessage) => void;
};

function WithdrawNegotiationOfferModal({
  open,
  onClose,
  brandNegotiationId,
  paidGigId,
  networkPrettyName,
  snapshot,
  onWithdrawSuccess,
}: WithdrawNegotiationOfferModalProps) {
  const withdrawNegotiation = useWithdrawBrandNegotiation({paidGigId});
  const costChanged = snapshot.costInCents !== snapshot.previousCostInCents;
  const timePeriodChanged =
    snapshot.contentAgreementTimePeriodInMonths != null &&
    snapshot.contentAgreementTimePeriodInMonths !==
      snapshot.previousContentAgreementTimePeriodInMonths;

  const handleConfirm = () => {
    withdrawNegotiation.mutate(
      {id: brandNegotiationId},
      {
        onSuccess: (response) => {
          onClose();
          const newMessage = response?.data as ConversationMessage | undefined;
          if (newMessage && onWithdrawSuccess) {
            onWithdrawSuccess(newMessage);
          }
          raiseFlashMessage({
            status: 'success',
            mode: 'light',
            message: (
              <FlashMessage.Banner title={translate('Offer withdrawn')} />
            ),
          });
        },
      }
    );
  };

  return (
    <Modal open={open} handleClose={onClose} size="small">
      <Stack gap="16px">
        <Headline size="sm">
          {translate('Are you sure you want to withdraw?')}
        </Headline>
        <SnapshotCard
          sx={{borderColor: designSystemToken('semantic.bg.neutral-secondary')}}
        >
          <SectionHeader
            size="sm"
            color={designSystemToken('semantic.fg.secondary')}
          >
            {translate('Payment')}
          </SectionHeader>
          <ValuesRow>
            {costChanged ? (
              <StruckBody
                size="sm"
                color={designSystemToken('semantic.fg.secondary')}
              >
                {formatCentsToDollars(snapshot.previousCostInCents)}
              </StruckBody>
            ) : null}
            <Body size="sm" color={designSystemToken('semantic.fg.primary')}>
              {formatCentsToDollars(snapshot.costInCents)}
            </Body>
          </ValuesRow>
          {snapshot.contentAgreementTimePeriodInMonths !== null ? (
            <>
              <SectionHeader
                size="sm"
                color={designSystemToken('semantic.fg.secondary')}
              >
                {translate('Usage Rights Period')}
              </SectionHeader>
              <ValuesRow>
                {timePeriodChanged &&
                snapshot.previousContentAgreementTimePeriodInMonths != null ? (
                  <StruckBody
                    size="sm"
                    color={designSystemToken('semantic.fg.secondary')}
                  >
                    {translate('month', {
                      count:
                        snapshot.previousContentAgreementTimePeriodInMonths,
                    })}
                  </StruckBody>
                ) : null}
                <Body
                  size="sm"
                  color={designSystemToken('semantic.fg.primary')}
                >
                  {translate('month', {
                    count: snapshot.contentAgreementTimePeriodInMonths,
                  })}
                </Body>
              </ValuesRow>
            </>
          ) : null}
          {snapshot.gigDeliverableChanges.length > 0 ? (
            <>
              <SectionHeader
                size="sm"
                color={designSystemToken('semantic.fg.secondary')}
              >
                {translate('Deliverables')}
              </SectionHeader>
              <ValuesRow>
                {snapshot.gigDeliverableChanges.map((change) => (
                  <GigDeliverableChangeRow
                    key={change.kind}
                    change={change}
                    networkPrettyName={networkPrettyName}
                  />
                ))}
              </ValuesRow>
            </>
          ) : null}
        </SnapshotCard>
        <Body size="sm" color={designSystemToken('semantic.fg.secondary')}>
          {translate(
            'Withdrawing this offer will notify the creator and the current contract terms will be upheld.'
          )}
        </Body>
        <ButtonGroup>
          <Button
            mode="tinted"
            appearance="danger"
            size="medium"
            fullWidth
            onClick={handleConfirm}
            disabled={withdrawNegotiation.isLoading}
          >
            {translate('Yes, withdraw my offer')}
          </Button>
          <Button
            mode="tinted"
            appearance="neutral"
            size="medium"
            fullWidth
            onClick={onClose}
            disabled={withdrawNegotiation.isLoading}
          >
            {translate('No, cancel withdraw')}
          </Button>
        </ButtonGroup>
      </Stack>
    </Modal>
  );
}

export default WithdrawNegotiationOfferModal;
